import { X } from 'lucide-react'
import type { MetricLineage } from './types'
import { DETAIL_VERB } from './sourceRoute'

export function SourceLineageSidebar({
  lineage,
  onClose,
  onOpenSource,
  onFieldDetail,
}: {
  lineage: MetricLineage
  onClose: () => void
  /** Open the document a stated operand came from. */
  onOpenSource?: (fieldName: string | null) => void
  /** Show a Derived, Suggested or Confirmed operand's formula and inputs,
   *  basis or confirmation. Such an operand has no document to open. */
  onFieldDetail?: (fieldName: string) => void
}) {
  return (
    <aside className="flex h-full w-[248px] shrink-0 flex-col border-l border-stone-800/80 bg-[#080808]">
      <header className="flex items-start justify-between border-b border-stone-800/80 px-3 py-2">
        <div>
          <div className="font-mono text-[8px] font-semibold uppercase tracking-[0.16em] text-amber-300/90">Source Lineage</div>
          <div className="mt-0.5 font-mono text-[13px] font-medium text-stone-100">
            {lineage.label} {lineage.displayValue}
          </div>
        </div>
        <button type="button" onClick={onClose} className="rounded p-0.5 text-stone-600 hover:text-stone-300">
          <X size={14} />
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-auto px-3 py-2 space-y-3">
        {lineage.formula ? (
          <section>
            <div className="mb-1 font-mono text-[8px] font-semibold uppercase tracking-wide text-amber-300/80">Formula</div>
            <div className="rounded border border-stone-800/80 bg-black/40 px-2 py-1.5 font-mono text-[10px] text-stone-300">
              {lineage.formula}
            </div>
          </section>
        ) : null}

        {lineage.operands.length > 0 ? (
          <section>
            <div className="mb-1 font-mono text-[8px] font-semibold uppercase tracking-wide text-amber-300/80">Operands</div>
            <ul className="space-y-1.5">
              {lineage.operands.map((op) => (
                <li key={op.label} className="rounded border border-stone-800/60 bg-black/30 px-2 py-1.5">
                  <div className="text-[9px] text-stone-600">{op.label}</div>
                  <div className="font-mono text-[11px] text-stone-200">{op.value}</div>
                  {op.source?.state && DETAIL_VERB[op.source.state] ? (
                    // Only a stated operand links to a document. A derived,
                    // suggested or confirmed one opens its formula and
                    // inputs, basis or confirmation, or reads as text.
                    onFieldDetail && op.source.fieldName ? (
                      <button
                        type="button"
                        onClick={() => onFieldDetail(op.source!.fieldName!)}
                        title={op.source.label}
                        aria-label={`Show how ${op.label} ${DETAIL_VERB[op.source.state]}`}
                        className="mt-0.5 block max-w-full truncate whitespace-nowrap text-left text-[9px] text-stone-400 underline decoration-stone-500 decoration-dotted underline-offset-2 hover:decoration-stone-300"
                      >
                        {op.source.label}
                      </button>
                    ) : (
                      <div className="mt-0.5 truncate text-[9px] text-stone-500">{op.source.label}</div>
                    )
                  ) : op.source ? (
                    <button
                      type="button"
                      onClick={() => onOpenSource?.(op.source?.fieldName ?? null)}
                      className="mt-0.5 truncate whitespace-nowrap text-[9px] text-sky-400 hover:underline"
                    >
                      {op.source.label}
                    </button>
                  ) : (
                    <div className="mt-0.5 text-[9px] text-stone-600">
                      {op.value === '—' ? 'Not on the deal' : 'Suggested — no document on file'}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <section>
          <div className="mb-1 font-mono text-[8px] font-semibold uppercase tracking-wide text-amber-300/80">Audit</div>
          <ul className="space-y-1 text-[9px] text-stone-500">
            <li>Last written by: {lineage.enteredBy ?? '—'}</li>
            {/* Confirmed means a person — never a machine backfill. */}
            <li>Confirmed by: {lineage.verifiedBy ?? 'not yet confirmed by a person'}</li>
            {lineage.historyNote ? <li>{lineage.historyNote}</li> : null}
          </ul>
        </section>
      </div>

      <footer className="shrink-0 border-t border-stone-800/80 px-3 py-2">
        <p className="text-[8px] text-stone-600">
          {lineage.deterministic ? 'Derived by a fixed formula' : 'As recorded on the deal'} · No AI · All changes logged
        </p>
      </footer>
    </aside>
  )
}
